import React, { useState, useMemo, useEffect } from "react";
import {
  Skeleton,
  Input,
  Select,
  message,
  Form,
  Button,
  Spin,
  Modal as AntdModal,
  Table,
  Checkbox,
  Tooltip,
} from "antd";
import { FaTrashAlt } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Timesheetapi } from "../../middleware/interceptor";

const parseStatus = (active) => {
  if (active === null || active === undefined) return "Active";
  if (active === 4) return "Inactive";
  return "Active";
};

// ── Location / Timesheet required (saved on the HR list: dbo.Employee_Master) ──
// Read:  api/User/getAllEmployees → country, timesheet_required, on_hr_list
// Save:  api/User/updateEmployeeSettings (Admin / HR only)
// Used by: holidays (India / US calendar) and Utilization / Organization (not-required people are left out)
const LOCATION_OPTIONS = [
  { value: "IN", label: "India" },
  { value: "US", label: "US" },
];
const SETTINGS_EDITOR_ROLE_IDS = [3, 4]; // Admin, HR (same as the holiday calendar)
const NOT_ON_HR = "Not on the HR list yet. Changing Location or Timesheet adds them to the HR list as \"Not on HR list\".";
const myEmpId = () => Number(localStorage.getItem("employee_id")) || 0;
const myRoleId = () => Number(localStorage.getItem("emp_role_id")) || 0;
const canEditSettings = () => SETTINGS_EDITOR_ROLE_IDS.includes(myRoleId());

export default function Users() {
  const [countsLoading, setCountsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [tempdata, setTempData] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState("");
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("Active"); // default: active people only
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [selectedTimesheet, setSelectedTimesheet] = useState("All");
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [roleUpdatingId, setRoleUpdatingId] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [allEmployeeOptions, setAllEmployeeOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isUpdateEnabled, setIsUpdateEnabled] = useState(false);
  const [currentAdminRow, setCurrentAdminRow] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [updatingEmployeeId, setUpdatingEmployeeId] = useState(null);
  const [azureLoading, setAzureLoading] = useState(false);
  const [baseCounts, setBaseCounts] = useState({ total: 0, active: 0, inactive: 0 });
  const [pageSize, setPageSize] = useState(10);
  const [settingSavingId, setSettingSavingId] = useState(null); // row whose Location / Timesheet is saving

  const EXCLUDED_ROLES = ["HR", "Accountant"];

  const countsOf = (list) => ({
    total: list.length,
    active: list.filter((u) => u.status === "Active").length,
    inactive: list.filter((u) => u.status === "Inactive").length,
  });

  const fetchRoles = async () => {
    try {
      const response = await Timesheetapi.get("api/User/getAllRoles");
      const roles = response.data?.Roles || [];
      const mapped = roles
        .filter((r) => !EXCLUDED_ROLES.includes(r.rolename))
        .map((r) => ({
          value: r.rolename,
          label: r.rolename,
          role_id: r.role_id,
        }));
      setRoleOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  const fetchEmployees = async () => {
    setCountsLoading(true);
    setTableLoading(true);
    try {
      const response = await Timesheetapi.get("api/User/getAllEmployees");
      if (response.data?.Error) console.error("getAllEmployees:", response.data.Error);
      const employees = (response.data?.Employees || []).map((emp) => ({
        // HR-list-only people (no timesheet login) have no employee_id → key by emp code / email
        User_id: emp.hr_only ? `hr:${emp.emp_code || emp.employee_email}` : emp.employee_id,
        hr_only: !!emp.hr_only,
        emp_code: emp.emp_code || "",
        designation: emp.designation || "",
        name: emp.employee_name || "-",
        email: emp.employee_email || "-",
        phone: emp.Emp_Phone || "-",
        role: emp.hr_only ? "" : emp.employee_role || " ",
        // Login: DB status first, replaced by the Azure status once that check finishes.
        // HR-list-only: status from the HR dates (no exit date / exit in the future = Active)
        status: emp.hr_only
          ? emp.hr_status || "Active"
          : String(emp.Del_emp_status) === "1" ? "Inactive" : parseStatus(emp.Active),
        // HR list settings
        country: emp.country || "IN",
        timesheet_required: emp.timesheet_required !== false,
        on_hr_list: !!emp.on_hr_list,
        team_members: [],
        _raw: emp,
      }));

      // UI only: people without an email are not shown (the API still returns them)
      const hasEmail = (e) => !!String(e._raw.employee_email || "").trim();
      const sorted = employees.filter(hasEmail).sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

      // Team member choices: only people with a login
      const allOptions = sorted.filter((e) => !e.hr_only).map((e) => ({
        value: e.User_id,
        label: e.name,
        role: e.role,
      }));
      setAllEmployeeOptions(allOptions);
      setEmployeeOptions(allOptions.filter((e) => e.role !== "Admin"));

      const merged = await fetchTeamMembers(sorted);
      setTempData(merged);
      setRawData(merged);
      setBaseCounts(countsOf(merged));

      // Azure status after employees load (not in Azure AD → Inactive)
      fetchAzureStatuses(merged);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      toast.error("Failed to load users. Please try again.");
    } finally {
      setTableLoading(false);
      setCountsLoading(false);
    }
  };

  const fetchAzureStatuses = async (employeeList) => {
    setAzureLoading(true);
    try {
      // Azure decides the status of people with a login; HR-list-only people keep their HR status
      const emails = employeeList
        .filter((emp) => !emp.hr_only && emp.email && emp.email !== "-")
        .map((emp) => emp.email);

      const res = await Timesheetapi.post("api/User/getAzureStatusBatch", { emails });

      if (!res.data?.success) {
        console.warn("Azure status check failed, showing database status:", res.data?.message);
        return; // keep the DB status set in fetchEmployees
      }

      const statusMap = {};
      (res.data.data || []).forEach((item) => {
        // "Unknown" (Graph error for that person) → keep the DB status
        if (item.status === "Active" || item.status === "Inactive") statusMap[item.email] = item.status;
      });

      // Merge into the CURRENT rows (a Location / Timesheet change may have happened meanwhile)
      setTempData((prev) => {
        const updated = prev.map((emp) => (emp.hr_only ? emp : { ...emp, status: statusMap[emp.email] ?? emp.status }));
        setBaseCounts(countsOf(updated));
        return updated;
      });
    } catch (err) {
      console.error("Azure batch status check failed:", err);
    } finally {
      setAzureLoading(false);
    }
  };

  const fetchTeamMembers = async (employeeList) => {
    try {
      const response = await Timesheetapi.get("api/User/getAllTeamMembers");
      if (!response.data?.success) return employeeList;

      const allRows = response.data.data || [];

      const merged = employeeList.map((emp) => {
        const roleLower = (emp.role || "").toLowerCase();
        const empId = parseInt(emp.User_id);
        let preSelected = [];

        if (roleLower === "admin") {
          allRows
            .filter((r) => Number(r.admin_id) === empId)
            .forEach((row) => {
              preSelected = [
                ...preSelected,
                ...(row.manager_ids || []).map(Number),
                ...(row.teamlead_ids || []).map(Number),
                ...(row.member_ids || []).map(Number),
              ];
            });
        } else if (roleLower === "project manager") {
          // The PM's OWN row (no admin, PM first) — same rule as the API
          allRows
            .filter((r) => !r.admin_id && Number((r.manager_ids || [])[0]) === empId)
            .forEach((row) => {
              preSelected = [
                ...preSelected,
                ...(row.teamlead_ids || []).map(Number),
                ...(row.member_ids || []).map(Number),
              ];
            });
        } else if (roleLower === "team lead") {
          // The TL's OWN row (no admin, no PM, TL first)
          allRows
            .filter((r) => !r.admin_id && !(r.manager_ids || []).length && Number((r.teamlead_ids || [])[0]) === empId)
            .forEach((row) => {
              preSelected = [...preSelected, ...(row.member_ids || []).map(Number)];
            });
        }

        preSelected = [...new Set(preSelected)].filter((id) => id !== empId);
        return { ...emp, team_members: preSelected };
      });

      return merged;
    } catch (err) {
      console.error("Failed to fetch team members:", err);
      return employeeList;
    }
  };

  const handleTeamMembersChange = (userId, newMembers, allOptions) => {
    const byRole = (role) =>
      newMembers.filter((id) => allOptions.find((e) => e.value === id)?.role?.toLowerCase() === role);
    const managerIds = byRole("project manager");
    const teamleadIds = byRole("team lead");
    const memberIds = byRole("team member");

    setUpdatingEmployeeId(userId);
    setSelectedMembers(newMembers);
    setIsUpdateEnabled(newMembers.length > 0);

    setCurrentAdminRow({ managerIds, teamleadIds, memberIds });
    setTempData((prev) =>
      prev.map((u) => (u.User_id === userId ? { ...u, team_members: newMembers } : u))
    );
  };

  const handleSaveTeamMembers = async () => {
    setSaveLoading(true);
    try {
      const employeeId = updatingEmployeeId;
      const emp = allEmployeeOptions.find((e) => e.value === employeeId);
      const roleLower = (emp?.role || "").toLowerCase();

      const managerIds = currentAdminRow?.managerIds || [];
      const teamleadIds = currentAdminRow?.teamleadIds || [];
      const memberIds = currentAdminRow?.memberIds || [];

      let payload = {};

      if (roleLower === "admin") {
        payload = {
          admin_id: employeeId,
          manager_ids: JSON.stringify(managerIds),
          teamlead_ids: JSON.stringify(teamleadIds),
          member_ids: JSON.stringify(memberIds),
        };
      } else if (roleLower === "project manager") {
        payload = {
          admin_id: null,
          manager_ids: JSON.stringify([employeeId]),
          teamlead_ids: JSON.stringify(teamleadIds),
          member_ids: JSON.stringify(memberIds),
        };
      } else if (roleLower === "team lead") {
        payload = {
          admin_id: null,
          manager_ids: null,
          teamlead_ids: JSON.stringify([employeeId]),
          member_ids: JSON.stringify(memberIds),
        };
      } else {
        toast.error("Only Admin, Project Manager and Team Lead have team members.");
        return;
      }

      const response = await Timesheetapi.post("api/User/updateTeamMembers", payload);

      if (response.data?.success) {
        toast.success("Team members saved successfully!");
        setIsUpdateEnabled(false);
        setUpdatingEmployeeId(null);
        await fetchEmployees();
      } else {
        toast.error(response.data?.message || "Save failed.");
      }
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to save team members.");
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Location / Timesheet required: save one change, show it at once, undo it if the save fails ──
  const handleSettingChange = async (record, change) => {
    const before = { country: record.country, timesheet_required: record.timesheet_required };
    setSettingSavingId(record.User_id);
    setTempData((prev) => prev.map((u) => (u.User_id === record.User_id ? { ...u, ...change } : u)));
    try {
      const res = await Timesheetapi.post("api/User/updateEmployeeSettings", {
        empid: myEmpId(),
        roleId: myRoleId(),
        // login → employee_id; HR-list-only → emp_code / email
        ...(record.hr_only
          ? { employee_id: 0, emp_code: record.emp_code || null, email: record.email !== "-" ? record.email : null }
          : { employee_id: record.User_id }),
        ...change,
      });
      if (!res.data?.success) throw new Error(res.data?.message || "Save failed");
      if (res.data.added_to_hr_list) {
        // Now on Employee_Master (as "Not on HR list") → later changes update that row
        setTempData((prev) => prev.map((u) => (u.User_id === record.User_id ? { ...u, on_hr_list: true } : u)));
      }

      if ("country" in change) {
        toast.success(`${record.name}: ${change.country === "US" ? "US" : "India"} holidays`);
      } else {
        toast.success(
          change.timesheet_required
            ? `${record.name} is tracked again`
            : `${record.name} is left out of Utilization and missed entries`
        );
      }
    } catch (err) {
      console.error("Setting save failed:", err);
      setTempData((prev) => prev.map((u) => (u.User_id === record.User_id ? { ...u, ...before } : u)));
      toast.error(err.message || "Failed to save. Please try again.");
    } finally {
      setSettingSavingId(null);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchEmployees();
  }, []);

  const applyFilters = () => {
    let filtered = [...tempdata];
    if (selectedRole !== "All") filtered = filtered.filter((u) => u.role === selectedRole);
    if (selectedStatus !== "All") filtered = filtered.filter((u) => u.status === selectedStatus);
    if (selectedLocation !== "All") filtered = filtered.filter((u) => (u.country || "IN") === selectedLocation);
    if (selectedTimesheet !== "All")
      filtered = filtered.filter((u) => (u.timesheet_required !== false) === (selectedTimesheet === "Required"));
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.designation || "").toLowerCase().includes(q) ||
          (u.role || "").toLowerCase().includes(q) ||
          (u.status || "").toLowerCase().includes(q)
      );
    }
    setRawData(filtered);
  };

  useEffect(() => { applyFilters(); }, [searchText, selectedRole, selectedStatus, selectedLocation, selectedTimesheet, tempdata]);
  useEffect(() => { setData(rawData); }, [rawData]);

  const handleRoleChange = async (userId, newRoleName) => {
    const selectedRoleObj = roleOptions.find((r) => r.value === newRoleName);
    if (!selectedRoleObj) return;

    const currentEmp = tempdata.find((u) => u.User_id === userId);
    const oldRoleName = currentEmp?.role || "";

    if (userId === myEmpId()) {
      toast.warn("You can't change your own role. Ask another admin.");
      return;
    }
    // Confirm before giving or removing Admin
    const touchesAdmin = [oldRoleName, newRoleName].some((r) => (r || "").toLowerCase() === "admin");
    if (touchesAdmin) {
      const ok = await new Promise((resolve) =>
        AntdModal.confirm({
          title: `Change ${currentEmp?.name || "this user"} from "${oldRoleName}" to "${newRoleName}"?`,
          content: "Admins can see and change everyone's data. Other admins keep their access.",
          okText: "Yes, change role",
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      );
      if (!ok) return;
    }

    setRoleUpdatingId(userId);
    try {
      // STEP 1: role on the Employee row (empid = who is changing it; the API refuses own role / last Admin)
      const response = await Timesheetapi.post("api/User/updateEmployeeRole", {
        employee_id: userId,
        role_id: selectedRoleObj.role_id,
        empid: myEmpId(),
      });

      if (response.data?.success) {
        setTempData((prev) => prev.map((u) => (u.User_id === userId ? { ...u, role: newRoleName } : u)));
        toast.success(`Role updated to "${newRoleName}" successfully`);

        // STEP 2: move them between the team lists (Employee_Reporting)
        try {
          const mig = await Timesheetapi.post("api/User/migrateEmployeeRole", {
            employee_id: userId,
            old_role: oldRoleName,
            new_role: newRoleName,
          });
          if (!mig.data?.success) {
            console.error("Role migration failed:", mig.data?.message);
            toast.warn("Role changed, but team lists weren't updated. Check Team Members.");
          }
        } catch (migrateErr) {
          console.error("Role migration failed:", migrateErr);
          toast.warn("Role changed, but team lists weren't updated. Check Team Members.");
        }
        await fetchEmployees();
      } else {
        toast.error(response.data?.message || "Failed to update role");
      }
    } catch (err) {
      console.error("Role update failed:", err);
      toast.error("Failed to update role. Please try again.");
    } finally {
      setRoleUpdatingId(null);
    }
  };

  const openCreateModal = () => {
    setIsModalOpen(true);
    setEdit(false);
    setEditId(null);
    form.resetFields();
  };

  const openDeleteModal = (record) => {
    setDeleteId(record.User_id);
    setDeleteUserName(record.name);
    setDeleteModal(true);
  };

  const handleSubmit = async (values) => {
    setModalLoading(true);
    try {
      const selectedRoleObj = roleOptions.find((r) => r.value === values.role);

      const response = await Timesheetapi.post("api/User/createNewUser", {
        name: values.name,
        email: values.email,
        phone: values.contact,
        status: values.status,
        role_id: selectedRoleObj.role_id,
      });

      if (response.data?.success) {
        await fetchEmployees();
        toast.success("User added successfully.");
        setIsModalOpen(false);
        form.resetFields();
      } else if (response.data?.message?.toLowerCase().includes("email already exists")) {
        form.setFields([{ name: "email", errors: ["Email already exists. Please use a different email."] }]);
      } else {
        toast.error(response.data?.message || "Failed to create user");
      }
    } catch (err) {
      console.error("Create failed:", err);
      toast.error("Failed to create user. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  const confirmDelete = async () => {
    setModalLoading(true);
    try {
      const response = await Timesheetapi.post("api/User/deleteEmployee", { employee_id: deleteId });

      if (response.data?.success) {
        setTempData((prev) => prev.filter((u) => u.User_id !== deleteId));
        toast.success("User deleted successfully");
        setDeleteModal(false);
        setDeleteId(null);
        setDeleteUserName("");
      } else {
        toast.error(response.data?.message || "Failed to delete user");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete user. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  const STATUS_OPTIONS = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const columns = useMemo(
    () => [
      {
        title: "#",
        key: "index",
        width: 55,
        align: "center",
        render: (_, __, index) => <span className="text-[12px] text-gray-400">{index + 1}</span>,
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 220,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (val, record) => (
          <div className="flex flex-col">
            <span className="text-[13px] font-[500] text-gray-800">{val || "-"}</span>
            {record.hr_only && (
              <Tooltip title="On the HR list (Employee_Master) but has no Timesheet login. Only Location and Timesheet can be set.">
                <span className="text-[11px] text-[#B45309]">
                  HR list only · no login{record.designation ? ` · ${record.designation}` : ""}
                </span>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 220,
        render: (val) => <span className="text-[13px] text-gray-700">{val || "-"}</span>,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: 150,
        render: (val) => <span className="text-[13px] text-gray-700">{val || "-"}</span>,
      },
      {
        title: "Role",
        dataIndex: "role",
        key: "role",
        width: 200,
        render: (val, record) => record.hr_only ? (
          <span className="text-[12px] text-gray-400">No login</span>
        ) : (
          <Select
            value={val}
            size="small"
            style={{ width: 160, height: "30px" }}
            options={roleOptions}
            disabled={record.status !== "Active" || record.User_id === myEmpId()}
            onChange={(newVal) => handleRoleChange(record.User_id, newVal)}
          />
        ),
      },
      {
        title: "Team Members",
        dataIndex: "team_members",
        key: "team_members",
        width: 220,
        render: (val, record) => {
          const roleLower = (record.role || "").toLowerCase();

          const activeEmployeeOptions = allEmployeeOptions.filter((e) => {
            const emp = tempdata.find((t) => t.User_id === e.value);
            return emp?.status === "Active";
          });

          let filteredOptions = [];

          if (roleLower === "admin") {
            filteredOptions = activeEmployeeOptions.filter(
              (e) => e.value !== record.User_id && e.role?.toLowerCase() !== "admin"
            );
          } else if (roleLower === "project manager") {
            const myTeam = val || [];
            filteredOptions = activeEmployeeOptions
              .filter(
                (e) =>
                  myTeam.includes(e.value) ||
                  e.role?.toLowerCase() === "team lead" ||
                  e.role?.toLowerCase() === "team member"
              )
              .filter(
                (e) =>
                  e.value !== record.User_id &&
                  e.role?.toLowerCase() !== "admin" &&
                  e.role?.toLowerCase() !== "project manager"
              );
          } else if (roleLower === "team lead") {
            const myTeam = val || [];
            filteredOptions = activeEmployeeOptions
              .filter((e) => myTeam.includes(e.value) || e.role?.toLowerCase() === "team member")
              .filter((e) => e.value !== record.User_id);
          }

          const isDisabled = record.hr_only || roleLower === "team member" || record.status === "Inactive";

          const activeVal = (val || []).filter((id) => {
            const emp = tempdata.find((t) => t.User_id === id);
            return emp?.status === "Active";
          });

          return (
            <Select
              mode="multiple"
              size="small"
              value={activeVal}
              disabled={isDisabled}
              placeholder={isDisabled ? "N/A" : "Select members"}
              style={{ width: "100%", minWidth: 180, height: "32px" }}
              options={filteredOptions}
              onChange={(newVal) => handleTeamMembersChange(record.User_id, newVal, allEmployeeOptions)}
              showSearch
              optionFilterProp="label"
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) =>
                omittedValues.length > 0 ? <span>{omittedValues.length} Employees</span> : null
              }
            />
          );
        },
      },
      {
        title: "Location",
        dataIndex: "country",
        key: "country",
        width: 120,
        render: (val, record) => {
          const select = (
            <Select
              value={val || "IN"}
              size="small"
              style={{ width: 96, height: "30px" }}
              options={LOCATION_OPTIONS}
              loading={settingSavingId === record.User_id}
              disabled={!canEditSettings() || settingSavingId === record.User_id}
              onChange={(country) => handleSettingChange(record, { country })}
            />
          );
          return record.on_hr_list ? select : <Tooltip title={NOT_ON_HR}>{select}</Tooltip>;
        },
      },
      {
        title: "Timesheet",
        dataIndex: "timesheet_required",
        key: "timesheet_required",
        width: 100,
        align: "center",
        render: (val, record) => (
          <Tooltip
            title={
              !record.on_hr_list
                ? `${val !== false ? "Timesheet required" : "Not required"}. ${NOT_ON_HR}`
                : val !== false
                  ? "Timesheet required: counted in Utilization and missed entries"
                  : "Not required: left out of Utilization and missed entries"
            }
          >
            <Checkbox
              checked={val !== false}
              disabled={!canEditSettings() || settingSavingId === record.User_id}
              onChange={(e) => handleSettingChange(record, { timesheet_required: e.target.checked })}
            />
          </Tooltip>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 160,
        render: (val) => (
          <Select value={val} size="small" style={{ width: 100, height: "30px" }} options={STATUS_OPTIONS} disabled />
        ),
      },
      {
        title: "Action",
        key: "action",
        width: 80,
        align: "center",
        fixed: "right",
        render: (_, record) => record.hr_only ? null : (
          <div className="flex justify-center gap-3">
            <FaTrashAlt onClick={() => openDeleteModal(record)} className="text-[14px] text-[#ff4d4d] cursor-pointer" />
          </div>
        ),
      },
    ],
    [data, roleOptions, roleUpdatingId, employeeOptions, allEmployeeOptions, tempdata, settingSavingId]
  );

  return (
    <div>
      <ToastContainer />
      {contextHolder}

      <Spin
        spinning={roleUpdatingId !== null || saveLoading || azureLoading}
        tip={azureLoading ? "Checking user status..." : saveLoading ? "Saving..." : "Updating role..."}
        size="large"
      >
        <div className="mx-auto px-0">
          <div className="mb-[5px]">
            <h2 className="page-heading relative bottom-[-10px]">USERS</h2>
          </div>
          <div className="mt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="text-center">
                  <span className="text-[12px] font-[500] text-gray-600">Total Users</span>
                  <div className="text-[28px] font-[600] text-blue-600 mt-2">
                    {countsLoading ? (
                      <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" />
                    ) : (
                      baseCounts.total
                    )}
                  </div>
                  <span className="text-[11px] text-gray-500 block mt-1">All Roles</span>
                </div>
              </div>

              <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="text-center">
                  <span className="text-[12px] font-[500] text-gray-600">Active Users</span>
                  <div className="text-[28px] font-[600] text-green-600 mt-2">
                    {countsLoading ? (
                      <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" />
                    ) : (
                      baseCounts.active
                    )}
                  </div>
                  <span className="text-[11px] text-gray-500 block mt-1">Status: Active</span>
                </div>
              </div>

              <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="text-center">
                  <span className="text-[12px] font-[500] text-gray-600">Inactive Users</span>
                  <div className="text-[28px] font-[600] text-red-600 mt-2">
                    {countsLoading ? (
                      <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" />
                    ) : (
                      baseCounts.inactive
                    )}
                  </div>
                  <span className="text-[11px] text-gray-500 block mt-1">Status: Inactive</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-end gap-[15px] mt-3 z-[9]">
            <div className="flex items-end gap-2 flex-wrap">
              <button
                onClick={openCreateModal}
                className="bg-blue-500 text-white py-[5px] px-3 rounded flex items-center justify-center w-[145px] h-[32px]"
              >
                <span className="mr-2">
                  <i className="fa fa-plus-circle text-[16px] relative top-[1px]"></i>
                </span>
                <span className="text-[13px]">Create a User</span>
              </button>
              <div className="w-[175px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Role:</label>
                <Select
                  value={selectedRole}
                  onChange={(val) => setSelectedRole(val)}
                  options={[{ value: "All", label: "All" }, ...roleOptions]}
                  placeholder="Select Role"
                />
              </div>
              <div className="w-[130px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Status:</label>
                <Select
                  value={selectedStatus}
                  onChange={(val) => setSelectedStatus(val)}
                  options={[{ value: "All", label: "All" }, ...STATUS_OPTIONS]}
                  placeholder="Select Status"
                />
              </div>
              <div className="w-[120px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Location:</label>
                <Select
                  value={selectedLocation}
                  onChange={(val) => setSelectedLocation(val)}
                  options={[{ value: "All", label: "All" }, ...LOCATION_OPTIONS]}
                />
              </div>
              <div className="w-[140px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Timesheet:</label>
                <Select
                  value={selectedTimesheet}
                  onChange={(val) => setSelectedTimesheet(val)}
                  options={[
                    { value: "All", label: "All" },
                    { value: "Required", label: "Required" },
                    { value: "Not required", label: "Not required" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px]">Search Users</label>
                <Input
                  placeholder="Search by Name, Role, or Status..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  prefix={<i className="fa fa-search text-gray-400"></i>}
                  className="h-[32px] w-[280px]"
                />
              </div>

              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px] opacity-0">Update</label>
                <Button
                  type="primary"
                  disabled={!isUpdateEnabled}
                  onClick={handleSaveTeamMembers}
                  className="h-[32px] w-[100px]"
                >
                  Update
                </Button>
              </div>
            </div>
          </div>

          <AntdModal
            className="workitem-popup"
            open={isModalOpen}
            footer={null}
            onCancel={() => setIsModalOpen(false)}
            closable
            maskClosable={false}
            keyboard={false}
            width={650}
          >
            <div className="bg-white rounded-lg">
              <h3
                className="text-[14px] font-[600] mb-4 px-[22px] mx-[-22px]"
                style={{ borderBottom: "1px solid #ddd", paddingBottom: "15px" }}
              >
                {edit ? "Update User" : "Create a User"}
              </h3>

              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <div className="task-project-modal">
                  <div className="grid grid-cols-2 gap-x-4">
                    <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter the user name" }]}>
                      <Input className="h-10" placeholder="Enter Full Name" />
                    </Form.Item>

                    <Form.Item label="Email" name="email" rules={[{ required: true, message: "Please enter the email" }]}>
                      <Input className="h-10" placeholder="Enter Email" />
                    </Form.Item>

                    <Form.Item
                      label="Contact"
                      name="contact"
                      rules={[{ required: true, message: "Please enter the phone number" }]}
                    >
                      <Input className="h-10" placeholder="Enter Phone Number" />
                    </Form.Item>

                    <Form.Item
                      label="Role"
                      name="role"
                      initialValue={roleOptions[0]?.value}
                      rules={[{ required: true, message: "Please select a role" }]}
                    >
                      <Select className="h-10" placeholder="Select Role" options={roleOptions} />
                    </Form.Item>

                    <Form.Item
                      label="Status"
                      name="status"
                      initialValue="Active"
                      rules={[{ required: true, message: "Please select a status" }]}
                    >
                      <Select className="h-10" placeholder="Select Status" options={STATUS_OPTIONS} />
                    </Form.Item>
                  </div>

                  <div className="text-right mt-6">
                    <Button
                      className="w-[100px]"
                      type="default"
                      onClick={() => setIsModalOpen(false)}
                      style={{ marginRight: "8px" }}
                    >
                      Cancel
                    </Button>
                    <Button className="w-[100px]" type="primary" htmlType="submit" loading={modalLoading}>
                      {edit ? (modalLoading ? "Updating..." : "Update") : modalLoading ? "Creating..." : "Create"}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </AntdModal>

          <AntdModal
            title={
              <>
                <ExclamationCircleFilled style={{ color: "orange", marginRight: "8px" }} />
                Confirm
              </>
            }
            open={deleteModal}
            onCancel={() => { setDeleteModal(false); setDeleteId(null); }}
            closable
            maskClosable={false}
            keyboard={false}
            footer={[
              <Button key="cancel" onClick={() => { setDeleteModal(false); setDeleteId(null); }}>
                Cancel
              </Button>,
              <Button key="confirm" type="primary" danger loading={modalLoading} onClick={confirmDelete}>
                Delete
              </Button>,
            ]}
          >
            <p>
              Are you sure you want to delete user <strong>"{deleteUserName}"</strong>?
            </p>
            <p>This action cannot be undone.</p>
          </AntdModal>

          {/* ── Table ── */}
          <div className="mt-5 overflow-auto custom-table absolute w-[98%] custom-user-table">
            <Spin spinning={tableLoading} tip="Loading...">
              <Table
                columns={columns}
                dataSource={data}
                rowKey="User_id"
                size="small"
                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total) => `Total ${total} users`,
                  onShowSizeChange: (current, size) => setPageSize(size),
                  onChange: (page, size) => setPageSize(size),
                }}
                bordered
                scroll={{ x: "max-content", y: 480 }}
                sticky={{ offsetHeader: 0 }}
                locale={{ emptyText: "No users found" }}
                rowClassName={(_, index) => (index % 2 === 0 ? "bg-white" : "bg-[#fafafa]")}
              />
            </Spin>
          </div>
        </div>
      </Spin>
    </div>
  );
}